/**
 * コマ別詳細テーブルの「日量合計」（48 コマ合計）算出。
 *
 * spec AC-PLC-048 [P0]（`docs/specs/plan-submit/plan-common.md`）:
 * 「コマ別詳細テーブルの時刻列先頭行（00:00 行）の直上に、スクロール中も常に表示される
 * 固定行で全数値列（需給バランス・計画種別固有の数値列すべて）の 48 コマ合計を表示する」。
 *
 * 用途は**取引先との計画値照合（不整合検知時の日量読み合わせ）**で、48 行スクロール
 * せずに総量を突合できるようにするもの（issue #6687）。
 *
 * ## 未入力（undefined）の扱い
 *
 * 各ページの `models/calc.ts` が持つ鉄則をそのまま縦方向へ適用する:
 *
 * > 構成内訳セルが 1 つでも空（undefined）なら合計も undefined（空）／空を 0 に潰さない
 *
 * 日量合計でも同じで、**48 コマに 1 つでも未入力があれば合計は undefined**（`emptyLabel`
 * 表示）にする。読み合わせが目的なので、欠損を 0 とみなして総量を出すと「入っている分の
 * 合計」なのか「全部入った上での合計」なのかが画面から区別できず、誤った突合につながる。
 *
 * ## backend との関係
 *
 * 合計は proto に乗らず提出もされない（`calc.ts` の注記どおり canonical な集計は backend の
 * OCCTO 提出時集計）。本関数は既に画面に出 している 48 コマ値の**表示上の再集計**に留まる。
 * spec も「表示中のコマ別データ（フィルタ・トグル切替後の値）に連動して再計算する」と
 * 表示連動を明示している。
 */

/** 48 コマの計画値。undefined は未入力（`calc.ts` の SlotValue と同義）。 */
export type SlotValue = number | undefined

/**
 * 表示中の 48 コマから日量合計を求める。
 *
 * @param slots コマ別の値。`slotCount` 個ぶんを見る。
 * @param slotCount 見るコマ数（= 描画中の行数。通常 48）。
 * @returns 全コマが数値なら合計。1 つでも未入力・`slotCount` が 0 なら undefined。
 */
export function calcDailyTotal(
  slots: readonly SlotValue[],
  slotCount: number,
): SlotValue {
  if (slotCount <= 0) return undefined
  let total = 0
  for (let i = 0; i < slotCount; i++) {
    const v = slots[i]
    // 空を 0 に潰さない（`calc.ts` の鉄則）。1 つでも欠ければ合計は出さない。
    if (v === undefined) return undefined
    total += v
  }
  return total
}
